import React, { useState } from "react";
import { Menu, Tabs, Tab, Box, Badge, CircularProgress } from "@mui/material";
import NotificationSVG from "../svg/NotificationFlagIcon";
import { TaskNotificationComponent } from "./TaskNotificationComponent";
import { useTranslation } from "react-i18next";
import moment from "moment/moment";

export const NotificationWrapperComponent = ({
  anchorEl,
  openMenu,
  closeMenu,
  notificationData,
  failedMessageData = [],
  handleResendMessage,
  resendingMessageId
}) => {
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = useState(0);

  const handleChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const findOffset = moment().tz(timeZone).utcOffset();
  const defaultZone = `(UTC ${findOffset}) ${timeZone}`;

  const convertToTimeZone = (requestDate) => {
    const currentUserLang = localStorage.getItem('i18nextLng') || 'en';
    let zone = sessionStorage.getItem('timeZone') || defaultZone;

    if (['null', null, 'undefined', undefined].includes(zone)) {
      zone = defaultZone;
    }

    const timeZoneName = zone.substring(zone.lastIndexOf(' ') + 1);

    const date = moment.utc(requestDate).tz(timeZoneName);
    return currentUserLang === 'en'
      ? date.format('MM/DD/YYYY hh:mm:ss A')
      : date.format('DD/MM/YYYY hh:mm:ss A');
  };


  // counters
  const failedCount = failedMessageData.length;
  const byDayCount = Object.values(notificationData).flat().length;

  return (
    <Menu
      id="menu-appbar-notification"
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={openMenu}
      onClose={closeMenu}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: "visible",
          width: "500px",
          filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
          mt: 7.3,
          "&:before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: "background.paper",
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
          },
        },
      }}
    >
      {/* Tabs with counters */}
      <Tabs
        value={tabIndex}
        onChange={handleChange}
        textColor="primary"
        indicatorColor="primary"
        variant="fullWidth"
        sx={{ borderBottom: "1px solid #ddd" }}
      >
        <Tab
          label={
            <Badge
              color="error"
              badgeContent={failedCount}
              invisible={failedCount === 0}
            >
              Undelivered Message
            </Badge>
          }
        />
        <Tab
          label={
            <Badge
              color="primary"
              badgeContent={byDayCount}
              invisible={byDayCount === 0}
            >
              Others
            </Badge>
          }
        />
      </Tabs>

      {/* Tab Content */}
      <Box
        sx={{
          ml: "10px",
          mr: "10px",
          height: "calc(75vh)",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        {/* Failed Messages */}
        {tabIndex === 0 && (
          <Box sx={{ p: 2 }}>
            {failedMessageData.length > 0 ? (
              failedMessageData.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid #ddd",
                    borderRadius: "12px",
                    boxShadow: 1,
                  }}
                >
                  {/* Header */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Box sx={{ fontWeight: "bold", fontSize: "14px" }}>
                      Failed {item.carrier} to {item.phone}
                    </Box>
                    <Box sx={{ fontSize: "12px", color: "gray" }}>
                      {convertToTimeZone(item.created_on || '')}
                    </Box>
                  </Box>

                  {/* Box */}
                  <Box
                    sx={{
                      fontSize: "13px",
                    }}
                  >
                    Box: {item.box}
                  </Box>


                  {/* Status */}
                  <Box
                    sx={{
                      fontSize: "13px",
                      color: "red",
                      fontStyle: "italic",
                      mt: 1,
                    }}
                  >
                    Status: {item.status}
                  </Box>

                  {/* Request Body */}
                  <Box sx={{ fontSize: "13px", mt: 1, whiteSpace: "pre-line" }}>
                    {item.request_body.replace(/"/g, "")}
                  </Box>

                  {/* Action */}
                  <Box textAlign="right" mt={2}>
                    <button
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #4caf50",
                        borderRadius: "6px",
                        background: "white",
                        color: "#4caf50",
                        cursor: "pointer",
                      }}
                      disabled={resendingMessageId === item.id}
                      onClick={() => handleResendMessage(item)}
                    >
                      {resendingMessageId === item.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        "Resend"
                      )}
                    </button>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{ fontSize: 12, color: "#6B778C", textAlign: "center" }}>
                No failed messages
              </Box>
            )}
          </Box>
        )}

        {/* Day-wise Notifications */}
        {tabIndex === 1 && (
          <Box sx={{ p: 2 }}>
            {byDayCount > 0 ? (
              Object.entries(notificationData).map(([day, block], index) => (
                <React.Fragment key={index}>
                  <Box
                    component="p"
                    sx={{
                      fontSize: "12px",
                      fontWeight: "500",
                      lineHeight: "15px",
                      color: "#6B778C",
                      m: 0,
                      textTransform: "uppercase",
                      mb: "6px",
                    }}
                  >
                    {day}
                  </Box>
                  {block.map((iterator, idx) => (
                    <TaskNotificationComponent props={iterator} key={idx} />
                  ))}
                </React.Fragment>
              ))
            ) : (
              <Box
                sx={{
                  margin: "0px 24px",
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: "10px 0px 10px",
                }}
              >
                <NotificationSVG width="40" height="40" />
                <Box
                  component="p"
                  sx={{
                    fontSize: "14px",
                    lineHeight: "20px",
                    width: "225px",
                    textAlign: "center",
                  }}
                >
                  {t("Navbar.NotificationBody")}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Menu>
  );
};



// WEBPACK FOOTER //
// ./src/components/common/NotificationWrapperComponent.js